/**
 * Pure logic helpers for the WinValue → Mietwagen-Akte import flow (Issue #381).
 *
 * Extracted as pure functions so they can be covered by Vitest without a DOM.
 */

export type ImportCardMode = 'hidden' | 'button' | 'success';

export type AktionStatus = 'offen' | 'erledigt';

export interface AktionenStatus {
  mietvertrag: AktionStatus;
  fahrzeug: AktionStatus;
  uebergabe: AktionStatus;
}

type MietwagenStatusValue =
  | 'vertrag_offen'
  | 'uebergeben'
  | 'in_miete'
  | 'zurueckgegeben'
  | 'abgerechnet'
  | 'abgeschlossen'
  | null
  | undefined;

/**
 * Determines what the WinValueMietwagenImportCard renders.
 *
 *   hidden  — no winvalue_uid: akte is not WinValue-linked, card invisible
 *   button  — winvalue_uid set, no mietwagen link: show "Mietwagen-Akte anlegen"
 *   success — winvalue_uid set AND mietwagen_akte_id set: show action cards
 */
export function deriveImportCardMode(
  winvalueUid: string | null | undefined,
  mietwagenAkteId: string | null | undefined,
): ImportCardMode {
  if (!winvalueUid) return 'hidden';
  if (mietwagenAkteId) return 'success';
  return 'button';
}

const UEBERGABE_DONE_STATUSES: MietwagenStatusValue[] = [
  'uebergeben',
  'in_miete',
  'zurueckgegeben',
  'abgerechnet',
  'abgeschlossen',
];

/**
 * Derives status-pill state for the three action cards from the Mietwagen-Akte.
 *
 *   mietvertrag — "erledigt" once status advances past vertrag_offen
 *   fahrzeug    — "erledigt" when fahrzeug_id is set
 *   uebergabe   — "erledigt" when status is uebergeben or later
 */
export function deriveAktionenStatus(mwAkte: {
  mietwagen_status: MietwagenStatusValue;
  fahrzeug_id: string | null | undefined;
}): AktionenStatus {
  const status = mwAkte.mietwagen_status;
  const mietvertrag: AktionStatus = status ? 'erledigt' : 'offen';
  const fahrzeug: AktionStatus = mwAkte.fahrzeug_id ? 'erledigt' : 'offen';
  const uebergabe: AktionStatus = UEBERGABE_DONE_STATUSES.includes(status) ? 'erledigt' : 'offen';

  return { mietvertrag, fahrzeug, uebergabe };
}

interface GutachtenAkteForPrefill {
  id: string;
  business_unit?: string | null;
  kunde_name?: string | null;
  kennzeichen?: string | null;
  fahrzeug_marke?: string | null;
  fahrzeug_modell?: string | null;
  schadendatum?: string | null;
  schadenort?: string | null;
  versicherung?: string | null;
  schadennummer?: string | null;
  akten_nr?: string | null;
  winvalue_uid?: string | null;
  winvalue_ref?: string | null;
  [key: string]: unknown;
}

/**
 * Builds the insert payload for a new Mietwagen-Akte prefilled from a Gutachten-Akte.
 * Does NOT include linked_akte_id — that is set by the API after the INSERT.
 */
export function buildMietwagenPrefillFromGutachten(
  gutachten: GutachtenAkteForPrefill,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    business_unit: 'mietwagen',
  };

  const copy = (field: keyof GutachtenAkteForPrefill, targetField?: string) => {
    const val = gutachten[field];
    if (val != null && val !== '') {
      payload[targetField ?? (field as string)] = val;
    }
  };

  copy('kunde_name');
  copy('kennzeichen');
  copy('fahrzeug_marke');
  copy('fahrzeug_modell');
  copy('schadendatum');
  copy('schadenort');
  copy('versicherung');
  copy('schadennummer');
  copy('winvalue_uid');
  copy('winvalue_ref');

  if (gutachten.akten_nr) {
    payload.gutachten_aktenzeichen = gutachten.akten_nr;
  }

  return payload;
}
